// API client. The access token lives only in memory; the refresh token is an httpOnly cookie the
// browser sends to /api/v1/auth/refresh. A 401 triggers one silent refresh and a single retry.

export interface Problem {
  title: string;
  status: number;
  detail?: string;
  code?: string;
  errors?: { field: string; message: string }[];
  data?: unknown;
}

export class ApiError extends Error {
  constructor(readonly problem: Problem) {
    super(problem.detail ?? problem.title);
    this.name = 'ApiError';
  }
  get status() {
    return this.problem.status;
  }
  fieldError(field: string): string | undefined {
    return this.problem.errors?.find((e) => e.field === field)?.message;
  }
}

let accessToken: string | null = null;
let refreshTimer: ReturnType<typeof setTimeout> | undefined;
let refreshing: Promise<boolean> | null = null;
const listeners = new Set<(signedIn: boolean) => void>();

/** Server refusals that concern the whole session rather than one request (M10). */
export const ACCESS_CODES = new Set(['mfa_enrolment_required', 'ip_not_allowed']);
const accessListeners = new Set<(code: string) => void>();

export function onAccessProblem(fn: (code: string) => void): () => void {
  accessListeners.add(fn);
  return () => accessListeners.delete(fn);
}

export function onSessionChange(fn: (signedIn: boolean) => void): () => void {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

export function setSession(token: string | null, expiresIn?: number): void {
  accessToken = token;
  clearTimeout(refreshTimer);
  if (token && expiresIn) {
    // Refresh a minute before expiry so requests rarely hit a 401.
    refreshTimer = setTimeout(() => void refreshSession(), Math.max(expiresIn - 60, 30) * 1000);
  }
  listeners.forEach((l) => l(!!token));
}

/** Current access token (realtime handshake); null when signed out. */
export function currentAccessToken(): string | null {
  return accessToken;
}

export function hasSession(): boolean {
  return accessToken !== null;
}

let refreshOffline = false;

/** Whether the last session refresh failed for want of a network (not because the session ended). */
export function lastRefreshWasOffline(): boolean {
  return refreshOffline;
}

/** Exchanges the refresh cookie for a new access token (single flight). */
export function refreshSession(): Promise<boolean> {
  refreshing ??= (async () => {
    try {
      refreshOffline = false;
      const res = await fetch('/api/v1/auth/refresh', {
        method: 'POST',
        headers: { 'x-tatra-csrf': '1' },
        credentials: 'same-origin',
      });
      if (!res.ok) {
        setSession(null);
        return false;
      }
      const body = (await res.json()) as { accessToken: string; expiresIn: number };
      setSession(body.accessToken, body.expiresIn);
      return true;
    } catch {
      refreshOffline = true;
      return false;
    } finally {
      refreshing = null;
    }
  })();
  return refreshing;
}

async function toProblem(res: Response): Promise<Problem> {
  try {
    const body = (await res.json()) as Partial<Problem>;
    return { title: body.title ?? res.statusText, status: res.status, ...body } as Problem;
  } catch {
    return { title: res.statusText || 'Request failed', status: res.status };
  }
}

type Method = 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE';

export async function api<T>(
  method: Method,
  path: string,
  body?: unknown,
  extraHeaders?: Record<string, string>,
): Promise<T> {
  const send = () =>
    fetch(`/api/v1${path}`, {
      method,
      credentials: 'same-origin',
      headers: {
        ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
        ...(body !== undefined ? { 'Content-Type': 'application/json' } : {}),
        ...extraHeaders,
      },
      body: body !== undefined ? JSON.stringify(body) : undefined,
    });

  let res = await send();
  if (res.status === 401 && accessToken && !path.startsWith('/auth/login')) {
    if (await refreshSession()) res = await send();
  }
  if (!res.ok) {
    const problem = await toProblem(res);
    if (res.status === 403 && problem.code && ACCESS_CODES.has(problem.code) && path !== '/auth/me') {
      accessListeners.forEach((l) => l(problem.code!));
    }
    throw new ApiError(problem);
  }
  if (res.status === 204) return undefined as T;
  const type = res.headers.get('content-type') ?? '';
  return (type.includes('json') ? await res.json() : await res.text()) as T;
}

export const get = <T>(path: string) => api<T>('GET', path);
export const post = <T>(path: string, body?: unknown) => api<T>('POST', path, body ?? {});
export const patch = <T>(path: string, body: unknown) => api<T>('PATCH', path, body);
export const put = <T>(path: string, body: unknown) => api<T>('PUT', path, body);
export const del = <T>(path: string) => api<T>('DELETE', path);

export function errorMessage(e: unknown): string {
  if (e instanceof ApiError) return e.problem.detail ?? e.problem.title;
  if (e instanceof Error) return e.message;
  return 'Something went wrong';
}

/**
 * POSTs to an endpoint that answers with a file (exports) and hands it to the browser as a download.
 * Problems (no permission, over the row limit) are thrown as ApiError like any other call.
 */
export async function downloadFile(path: string, body: unknown): Promise<{ filename: string; rows: number }> {
  const send = () =>
    fetch(`/api/v1${path}`, {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(body),
    });
  let res = await send();
  if (res.status === 401 && accessToken && (await refreshSession())) res = await send();
  if (!res.ok) throw new ApiError(await toProblem(res));
  const disposition = res.headers.get('content-disposition') ?? '';
  const filename = /filename="([^"]+)"/.exec(disposition)?.[1] ?? 'export.csv';
  const url = URL.createObjectURL(await res.blob());
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.append(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 10_000);
  return { filename, rows: Number(res.headers.get('x-export-rows') ?? 0) };
}

export interface UploadedFile {
  id: string;
  fileName: string;
  contentType: string;
  sizeBytes: number;
}

/** Uploads one file to the files API under a category (the category decides who may and what types). */
export async function uploadFile(category: string, file: File): Promise<UploadedFile> {
  const send = () => {
    const body = new FormData();
    body.append('category', category);
    body.append('file', file);
    return fetch('/api/v1/files', {
      method: 'POST',
      credentials: 'same-origin',
      headers: accessToken ? { Authorization: `Bearer ${accessToken}` } : {},
      body,
    });
  };
  let res = await send();
  if (res.status === 401 && accessToken && (await refreshSession())) res = await send();
  if (!res.ok) throw new ApiError(await toProblem(res));
  return (await res.json()) as UploadedFile;
}
